import * as React from "react";
import { useState, useLayoutEffect } from "react";
import i18n from "i18n";
import {
  Typography,
  FormControl,
  InputLabel,
  Button,
  withStyles,
} from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { WBSStore } from "modules/WBS/WBSStore";
import PropertiesTable from "./PropertiesTable";
import DialogModal from "common/components/DialogModal";
import { ToolbarStore } from "modules/WBS/ToolbarStore";
import { ToolbarTaskStore } from "modules/WBS/ToolbarTaskStore";
import { CustomPropertyStore } from "modules/customProperties/CustomPropertyStore";
import { gtag } from "ga-gtag";

const styles = (theme) => ({
  displayForm: {
    display: "block",
    marginBottom: 25,
  },
  buttonsGroup: {
    paddingTop: "8px",
    fontFamily: "Roboto",
    fontSize: "11px",
    fontWeight: 400,
    color: "#e2e2e3",
  },
  button: {
    fontFamily: "Roboto",
    fontSize: "11px",
    color: "rgba(255, 255, 255, 0.6)",
    margin: "5px -2px",
    padding: "5px",
    letterSpacing: "1px",
    fontWeight: "400" as any,
    border: "1px solid #1D1D1D",
    background: "#272727",
    "&:hover": {
      background: "#353539",
    },
    borderRadius: "4px",
  },
  buttonAddIcon: {
    marginRight: theme.spacing.unit,
    fontSize: 20,
  },
});

type ButtonProps = {
  label: string;
  classes: any;
  addIcon?: boolean;
  styleExtra?: any;
  onClickFunction: any;
};

const ButtonOutlined = (props: ButtonProps) => (
  <Button
    variant="outlined"
    size="small"
    className={props.classes.button}
    style={{ ...props.styleExtra }}
    aria-haspopup="true"
    onClick={props.onClickFunction}
  >
    {props.label}
  </Button>
);

type IProps = {
  classes: any;
  lineIndex: number;
  type: "resources" | "eap";
  reloading: boolean;
};

let customPropertiesTemp: any;
const CustomProps = (props: IProps) => {
  const { classes } = props;
  const [editing, setEditing] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [idToDelete, setIdToDelete] = useState("");
  const [properties, setProperties] = useState(new Array());
  const fromResources = props.type === "resources";

  // Atualiza a lista de propriedades para obter os valores da linha selecionada atualmente
  useLayoutEffect(() => {
    handleClickCancel();
    CustomPropertyStore.setReloadToFinished();
  }, [props.lineIndex, props.reloading]);

  function getWBSCustomProperties() {
    setProperties(CustomPropertyStore.getSelectedRowCustomProperties());
    setIdToDelete("");
  }

  function handleClickCloseModal() {
    setOpenModal(false);
  }

  function handleClickOpenModal(ev, data) {
    setIdToDelete(data); //Valor que será recebido da tabela de propriedades
    setOpenModal(true);
  }

  function handleClickAdd() {
    CustomPropertyStore.addBlankCustomProperty();
    getWBSCustomProperties();
  }

  function handleClickCancel() {
    CustomPropertyStore.setRowCustomProperties(props.lineIndex, fromResources);
    // Copia o valor antigo para poder usar no undo
    customPropertiesTemp = copy(
      CustomPropertyStore.getSelectedRowCustomProperties()
    );

    setEditing(false);
    getWBSCustomProperties();
  }

  function copy(object: any = []): any {
    return object.map((obj) => ({ ...obj }));
  }

  function handleClickDelete() {
    CustomPropertyStore.deleteCustomProperty(idToDelete);
    getWBSCustomProperties();
  }

  function handleBlurTitle(ev, data) {
    let value = ev.target.value;
    CustomPropertyStore.updateCustomPropertyLabel(data, value);
  }

  function handleBlurValue(ev, data) {
    let value = ev.target.value;
    CustomPropertyStore.updateCustomPropertyValue(data, value);
  }

  function _syncCustomProperty() {
    gtag("event", "adicionar_propriedade_personalizada", {
      adicionar_propriedade_personalizada: 0,
    });

    const oldProperties = copy(customPropertiesTemp);
    const executedFunc = CustomPropertyStore.syncCustomProperties(
      props.lineIndex,
      fromResources
    )
      .then(() => {
        handleClickCancel();
      })
      .catch((err) => {
        console.log(
          "Erro ao salvar alterações das Propriedades Customizadas: ",
          err
        );
      });

    const undoData = {
      originalOperation: "syncCustomProperty",
      undoOperation: "syncCustomProperty",
      data: {
        undoFunction: {
          function: "syncCustomProperty",
          params: [props.lineIndex, fromResources, oldProperties],
          selectedLine: [],
        },
        originalFunction: {
          function: "syncCustomProperty",
          params: [props.lineIndex, fromResources, oldProperties],
        },
      },
    };

    const selectedData = WBSStore.selectedLine
      ? WBSStore.selectedLine.data
      : undefined;
    if (selectedData && selectedData.parentTask) {
      ToolbarTaskStore.handelActions(executedFunc, undoData);
    } else {
      executedFunc;
      ToolbarStore.handelActions(undoData);
    }
  }

  return (
    <React.Fragment>
      <DialogModal
        open={openModal}
        onClose={handleClickCloseModal}
        title={i18n.project.rightSideMenu.customProperties.deleteModal.title}
        contentText={
          i18n.project.rightSideMenu.customProperties.deleteModal.text
        }
        whiteTheme={true}
        enableCloseButton={false}
        inverseButtons={true}
        buttonConfirmLabel={
          i18n.project.rightSideMenu.customProperties.deleteModal.buttonConfirm
        }
        buttonConfirmColor="#FF0000"
        onClickConfirm={handleClickDelete}
      />
      <FormControl className={classes.displayForm}>
        <InputLabel shrink={true} style={{ width: "max-content" }}>
          {i18n.project.rightSideMenu.customProperties.title}
        </InputLabel>
        <PropertiesTable
          includeBlankValues={true}
          elements={[
            {
              title: "",
              props: properties,
            },
          ]}
          enableEdit={editing}
          formatNumbers={false}
          onTitleBlur={handleBlurTitle}
          onValueBlur={handleBlurValue}
          onDeleteClick={handleClickOpenModal}
        />
        {!editing ? (
          <Typography className={classes.buttonsGroup} component="div">
            <ButtonOutlined
              label={i18n.project.rightSideMenu.customProperties.editProperties}
              addIcon={true}
              classes={classes}
              onClickFunction={() => {
                setEditing(true);
              }}
            />
          </Typography>
        ) : (
          <React.Fragment>
            <Typography style={{ textAlign: "center" }} component="div">
              <ButtonOutlined
                label={i18n.project.rightSideMenu.customProperties.addProperty}
                addIcon={true}
                classes={classes}
                styleExtra={{ height: "25px", borderColor: "#B0AFB1" }}
                onClickFunction={handleClickAdd}
              />
            </Typography>
            <Typography
              className={classes.buttonsGroup}
              style={{ textAlign: "right" }}
              component="div"
            >
              <ButtonOutlined
                label={i18n.cancel}
                classes={classes}
                styleExtra={{ marginLeft: "8px" }}
                onClickFunction={handleClickCancel}
              />
              <ButtonOutlined
                label={i18n.save}
                classes={classes}
                styleExtra={{ marginLeft: "8px" }}
                onClickFunction={_syncCustomProperty}
              />
            </Typography>
          </React.Fragment>
        )}
      </FormControl>
    </React.Fragment>
  );
};

export default withStyles(styles)(CustomProps);



// WEBPACK FOOTER //
// ./src/modules/customProperties/components/CustomProperties.tsx